import type { CustomerSupportRequest } from '../customerSupportApi'
import { useShopI18n } from '../../i18n'
import { CustomerAreaLayoutShell } from './customer-area'
import {
  CustomerSupportErrorBanner,
  CustomerSupportHistoryCard,
  CustomerSupportMetricsSection,
  CustomerSupportRequestCard,
} from './customer-support'
import { useCustomerSupportPageController } from './customer-support/useCustomerSupportPageController'

type CustomerSupportPageProps = {
  supportRequests: CustomerSupportRequest[]
  supportLoading: boolean
  supportError: string
  supportSubmitting: boolean
  supportSuccess: string
  supportSubject: string
  supportMessage: string
  supportOrderId: string
  supportCategory: string
  supportPriority: string
  onSupportSubjectChange: (value: string) => void
  onSupportMessageChange: (value: string) => void
  onSupportOrderIdChange: (value: string) => void
  onSupportCategoryChange: (value: string) => void
  onSupportPriorityChange: (value: string) => void
  onSubmitSupportRequest: () => void
  onRetrySupportRequests: () => void
  onNavigate: (targetPath: string) => void
}

export const CustomerSupportPage = ({
  supportRequests,
  supportLoading,
  supportError,
  supportSubmitting,
  supportSuccess,
  supportSubject,
  supportMessage,
  supportOrderId,
  supportCategory,
  supportPriority,
  onSupportSubjectChange,
  onSupportMessageChange,
  onSupportOrderIdChange,
  onSupportCategoryChange,
  onSupportPriorityChange,
  onSubmitSupportRequest,
  onRetrySupportRequests,
  onNavigate,
}: CustomerSupportPageProps) => {
  const { language, locale, t, toLocalizedPath } = useShopI18n()
  const {
    sortedRequests,
    openCount,
    inProgressCount,
    resolvedCount,
    navItems,
    supportContext,
    supportCategoryOptions,
    supportPriorityOptions,
    supportOrderRef,
  } = useCustomerSupportPageController({
    supportRequests,
    supportOrderId,
    supportCategory,
    supportSubject,
    language,
    t,
    toLocalizedPath,
    onSupportSubjectChange,
    onSupportOrderIdChange,
    onSupportCategoryChange,
  })

  return (
    <CustomerAreaLayoutShell
      eyebrow={t('common.customerArea')}
      title={t('customer.support.title')}
      subtitle={t('customer.support.subtitle')}
      navItems={navItems}
      activeNavId="support"
      onNavigate={onNavigate}
      actions={[
        {
          id: 'go-orders',
          label: t('common.accountOrders'),
          onClick: () => onNavigate(toLocalizedPath('/dashboard/orders')),
          variant: 'secondary',
        },
        {
          id: 'go-dashboard',
          label: t('common.accountDashboard'),
          onClick: () => onNavigate(toLocalizedPath('/dashboard')),
          variant: 'secondary',
        },
      ]}
    >
      <CustomerSupportErrorBanner
        error={supportError}
        retryLabel={t('common.retry')}
        onRetry={onRetrySupportRequests}
      />

      <CustomerSupportMetricsSection
        openCount={openCount}
        inProgressCount={inProgressCount}
        resolvedCount={resolvedCount}
        openLabel={t('customer.support.kpi.open')}
        inProgressLabel={t('customer.support.kpi.inProgress')}
        resolvedLabel={t('customer.support.kpi.resolved')}
      />

      <section className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)]">
        <CustomerSupportRequestCard
          title={t('customer.support.newRequest')}
          subtitle={t('customer.support.formSubtitle')}
          supportContext={supportContext}
          contextLinkedOrderText={
            supportContext
              ? t('customer.support.contextLinkedOrder', { orderRef: supportOrderRef })
              : ''
          }
          contextOrderStatusText={
            supportContext && supportContext.statusLabel
              ? t('customer.support.contextOrderStatus', { status: supportContext.statusLabel })
              : ''
          }
          contextMainProductText={
            supportContext && supportContext.productHint
              ? t('customer.support.contextMainProduct', { product: supportContext.productHint })
              : ''
          }
          subjectLabel={t('customer.support.subject')}
          subjectPlaceholder={t('customer.support.subjectPlaceholder')}
          categoryLabel={t('customer.support.categoryLabel')}
          categoryOptions={supportCategoryOptions}
          priorityLabel={t('customer.support.priorityLabel')}
          priorityOptions={supportPriorityOptions}
          orderIdLabel={t('customer.support.orderId')}
          orderIdPlaceholder={t('customer.support.orderIdPlaceholder')}
          messageLabel={t('customer.support.message')}
          messagePlaceholder={t('customer.support.messagePlaceholder')}
          submitLabel={t('customer.support.submit')}
          submittingLabel={t('customer.support.submitting')}
          supportSubject={supportSubject}
          supportMessage={supportMessage}
          supportOrderId={supportOrderId}
          supportCategory={supportCategory}
          supportPriority={supportPriority}
          supportSubmitting={supportSubmitting}
          supportSuccess={supportSuccess}
          onSupportSubjectChange={onSupportSubjectChange}
          onSupportMessageChange={onSupportMessageChange}
          onSupportOrderIdChange={onSupportOrderIdChange}
          onSupportCategoryChange={onSupportCategoryChange}
          onSupportPriorityChange={onSupportPriorityChange}
          onSubmitSupportRequest={onSubmitSupportRequest}
        />

        <CustomerSupportHistoryCard
          title={t('customer.support.history')}
          subtitle={t('customer.support.historySubtitle')}
          loadingLabel={t('customer.support.loading')}
          emptyLabel={t('customer.support.empty')}
          openedAtLabel={(date) => t('customer.support.openedAt', { date })}
          openTicketDetailLabel={t('customer.support.openTicketDetail')}
          locale={locale}
          language={language}
          loading={supportLoading}
          requests={sortedRequests}
          previewFallback={t('customer.support.previewFallback')}
          neverLabel={t('common.never')}
          onOpenTicket={(requestId) => onNavigate(toLocalizedPath(`/dashboard/support/${encodeURIComponent(requestId)}`))}
        />
      </section>
    </CustomerAreaLayoutShell>
  )
}
